import { screenConnection as sC, RTCStreamSingleton } from '../socket';
import { endpointMatch } from '../util';
import { appActions, messageActions } from '../action';
import { SyncEvent, VoidSyncEvent } from 'ts-events';
import { logger } from './logger';
import {
  mapKeyEventToLiveKeyPayload,
  mapCodeToWinScanCode,
  KeyboardEventCode,
  LiveKeyPayload,
} from '../component/keyboard';
import { dialogEvents } from '../component';
import { copyTextToClipboard } from '../util/clipboard';

const rS = RTCStreamSingleton.getConnection();
let isScreenRegistered = false;

/**
 * Screenshare api definition and listener initialization.
 * @module
 */

let debug: any = {};
if (window['debug']) window['debug'].screen = debug;

export function eventPacket(type: string, action: string) {
  return {
    EventType: type,
    Action: action,
  };
}

/** Events that may be hooked to listen for frames and the like. */
export let screenEvents = {
  attendedAccessSettingsReceived: new SyncEvent<AttendedAccessSettings>(),
  disconnect: new SyncEvent(),
  frame: new SyncEvent<ScreenTile>(),
  frameData: new SyncEvent<FrameData>(),
  changedDisplay: new SyncEvent<ChangedDisplay>(),
  start: new SyncEvent(),
  displayListReceived: new SyncEvent<DisplayInfo[]>(),
  sessionListReceived: new SyncEvent<WindowsSessionInfo>(),
  clipboardChanged: new SyncEvent<string>(),
  cursorIconChanged: new SyncEvent<CursorInfo>(),
};

function toWindows(code) {
  //for our good friend, the alt key.
  if (code === 18) return 12;
  return code;
}

var clipboardState = {
  remoteClipboardText: null,
  systemClipboardText: null,
  systemPasteReady: false,
};

var textArea;
function sendClipboardAndKeyDown(code: KeyboardEvent, clipboardText: string) {
  console.log('start sendClipboardAndKeyDown');
  /*
    // for IE support !!! DIDN'T TEST so it's commented !!!
    if (window.clipboardData) {
        clipboardState.systemClipboardText = window.clipboardData.getData('Text');
        return;
    }
*/

  console.log('send clipboard text: ' + clipboardText);
  if (code.ctrlKey) {
    callEndpointViaWebRTCWithWSFallback('clipboarddata', [
      clipboardText,
      'CtrlV',
    ]);
  } else if (code.shiftKey) {
    callEndpointViaWebRTCWithWSFallback('clipboarddata', [
      clipboardText,
      'ShiftIns',
    ]);
  } else {
    // this branch is not used now
    // sC.callEndpoint('clipboarddata', clipboardText);
  }
}

function updateClipboard() {
  console.log('updateClipboard()');
  if (clipboardState.remoteClipboardText != null) {
    copyTextToClipboard(clipboardState.remoteClipboardText);
    clipboardState.remoteClipboardText = null;
  }
}

function startClipboardUpdater() {
  setTimeout(updateClipboard, 950);
}

export function isPasteHotkey(keyboardEvent: KeyboardEvent) {
  return (
    (keyboardEvent.ctrlKey && keyboardEvent.code === 'KeyV') ||
    (keyboardEvent.shiftKey && keyboardEvent.code === 'Insert')
  );
}

var mousePosition = null;
function sendMousePosition() {
  if (mousePosition != null) {
    let x = mousePosition.x;
    let y = mousePosition.y;
    mousePosition = null;
    console.log('move [' + x + ', ' + y + ']');
    callEndpointViaWebRTCWithWSFallback('mousemove', [y, x]);
  }
}

const callEndpointViaWebRTCWithWSFallback = (
  endpoint: string,
  args?: any[],
) => {
  if (rS.channelOpen) {
    rS.callEndpoint(endpoint, args);
  } else {
    sC.callEndpoint(endpoint, args);
  }
};

setInterval(sendMousePosition, 100);

/** Screenshare API exported directly. Generally self-explanitory.*/
export let screenShareApi = {
  start(targetSessionId: number) {
    console.log('startscreenshare');
    sC.callEndpoint('startscreenshare', targetSessionId);
    /*
        sC.send({
            endpoint: "startscreenshare",
            args: []
        })
        */
  },
  requestFrame() {
    sC.callEndpoint('fullframe');
    /*
        sC.send({
            endpoint: "fullframe",
            args: []
        })
        */
  },
  login() {
    sC.send({
      endpoint: 'login',
      args: ['password'],
    });
  },
  disconnect() {
    console.log('stopscreenshare');
    sC.callEndpoint('stopscreenshare');
  },
  mouse: {
    move(PointerX: number, PointerY: number) {
      mousePosition = { x: PointerX, y: PointerY };
    },
    leftClick(PointerX: number, PointerY: number) {
      console.log('leftClick');
      callEndpointViaWebRTCWithWSFallback('leftclick', [PointerX, PointerY]);
    },
    rightClick() {
      console.log('rightClick');
      callEndpointViaWebRTCWithWSFallback('rightclick');
    },
    rightUp() {
      console.log('rightup');
      callEndpointViaWebRTCWithWSFallback('rightup');
    },
    rightDown() {
      console.log('rightdown');
      callEndpointViaWebRTCWithWSFallback('rightdown');
      //startClipboardUpdater()
    },
    down() {
      console.log('mousedown');
      //startClipboardUpdater()
      callEndpointViaWebRTCWithWSFallback('mousedown');
    },
    up() {
      console.log('mouseup');
      callEndpointViaWebRTCWithWSFallback('mouseup');
    },
    wheel(delta: number) {
      callEndpointViaWebRTCWithWSFallback('mousescroll', [delta]);
    },
  },
  paste(code: KeyboardEvent, clipboardText: string) {
    sendClipboardAndKeyDown(code, clipboardText);
  },
  keyDown(e: KeyboardEvent) {
    console.debug('keydown', e);
    const liveKeyPayload = mapKeyEventToLiveKeyPayload(e);
    sendKeyDown(liveKeyPayload, e.keyCode);
  },
  keyUp(e: KeyboardEvent) {
    console.debug('keyup', e);
    const liveKeyPayload = mapKeyEventToLiveKeyPayload(e);
    sendKeyUp(liveKeyPayload, e.keyCode);
  },
  keyDownRaw(code: KeyboardEventCode, fallback: number) {
    console.debug('keyDownRaw', code);
    const scanCode = mapCodeToWinScanCode(code, () => fallback);
    sendKeyDown({ type: 'scanCode', scanCode, vkCode: fallback }, fallback);
  },
  keyUpRaw(code: KeyboardEventCode, fallback: number) {
    console.debug('keyUpRaw', code);
    const scanCode = mapCodeToWinScanCode(code, () => fallback);
    sendKeyUp({ type: 'scanCode', scanCode, vkCode: fallback }, fallback);
  },
  ctrlAltDel() {
    callEndpointViaWebRTCWithWSFallback('ctrlaltdel');
  },
  setMonitor(monitorName: string) {
    console.log('setactivemonitor');
    sC.callEndpoint('setactivemonitor', [monitorName]);
  },
  getAttendedAccessSettings() {
    console.log('RequestAttendedAccessSettings called.');
    sC.callEndpoint('RequestAttendedAccessSettings');
  },
  getScreenList() {
    console.log('getScreenList called');
    sC.callEndpoint('getscreenlist');
  },
  getSessionList() {
    console.log('getSessionList called');
    sC.callEndpoint('getsessionlist');
  },
  resetDownKeys() {
    console.log('resetdownkeys');
    callEndpointViaWebRTCWithWSFallback('resetdownkeys');
  },
  sendRemoteAccessRequest(
    showPresenceOnAccept: boolean,
    targetSession: number,
  ) {
    console.log('Sending remote access request.');
    sC.callEndpoint('requestScreenAccess', [
      showPresenceOnAccept,
      targetSession,
    ]);
  },
  setClipboardData(text: string) {
    console.log('clipboarddata');
    callEndpointViaWebRTCWithWSFallback('clipboarddata', [text, 0]);
  },
  showPresenceToUser(showPresenceToUser: boolean) {
    console.log('showpresencetouser', showPresenceToUser);
    callEndpointViaWebRTCWithWSFallback('showpresencetouser', [
      showPresenceToUser,
    ]);
  },
};

function sendKeyUp(liveKeyPayload: LiveKeyPayload, keyCodeFallback: number) {
  callEndpointViaWebRTCWithWSFallback('keyup', [
    [keyCodeFallback],
    [liveKeyPayload],
  ]);
}

function sendKeyDown(liveKeyPayload: LiveKeyPayload, keyCodeFallback: number) {
  callEndpointViaWebRTCWithWSFallback('keydown', [
    [keyCodeFallback],
    [liveKeyPayload],
  ]);
}

export function disconnect() {
  screenEvents.disconnect.post({});
  sC.disconnect();
}

// export function isTile(message): message is ScreenTile {
//   let { top, bottom, left, right, image } = message;
//   return (
//     [top, bottom, left, right].every(prop => typeof prop === 'number') &&
//     typeof image === 'string'
//   );
// }

/*
const packetGuards = {
    connectedToScreenShare(msg) {
        return msg.endpoint && msg.endpoint == 
    }
}
*/

/**
 * Function for registering the screenshare API with the connection and RTC stream.
 * Sets some listeners on the main socket and the datachannel.
 */
export function register() {
  if (isScreenRegistered) {
    return;
  }
  isScreenRegistered = true;
  rS.events.channelOpen.attach(() => {
    console.log('channel open');
    appActions.setRtc(true);
    logger.track('webrtc_opened');
    logger.trackOnetime('webrtc_connected', undefined, {
      timeSince: 'app_load',
    });
  });
  rS.events.channelClose.attach(() => {
    appActions.setRtc(false);
    logger.track('webrtc_closed', undefined, { timeSince: 'webrtc_opened' });
  });
  appActions.setRtc(rS.channelOpen);
  console.log('Channel is ', rS.channelOpen);
  //sC.fallbackListen(console.log.bind(console));

  sC.listenKeys<typeof sC>(endpointMatch, {
    /*
        connectedToScreenShare(msg, sc) {
            sc.unencrypt()
            console.log("Conncetedet to screen share")
            let {key, iv, encKey, encIV} = generateKey(msg.results.publicKey)
            sc.send({
                endpoint: "aesHandshake",
                args: [encKey, encIV]
            })
            sc.encrypt(key, iv)
        },
        */
    /*
        aesHandshake(msg, sc) {
            console.log("Screen share AES handshake dun.")
            console.log(msg)
            let password = settingsStore.getState().settings.ScreenShareService.ScreenSharePass
            console.log(password)
            console.log(settingsStore.getState())
            if (msg.results.shook) {
                if (password) {
                    console.log("Password good, logging in.")
                    sc.send({
                        endpoint: "login",
                        args: [password]
                    })
                }
                else {
                    messageActions.message({
                        style: "danger", 
                        text: "No screen share password. Set one!"
                    })
                }
            }
        },
        */
    /*
        login(msg, sc) {
            console.log("login")
            sc.ofb = true
            screenEvents.login.post({})
        },
        */
    startScreenShare(result) {
      //console.log(result)
      if (result.screenStreamStarted) {
        screenEvents.start.post({});
      } else {
        // This shouldn't ever happen, unless we get into a messed up state
        // or someone's sending packets they shouldn't.  So we'll just use a
        // generic error and refresh the window.
        dialogEvents.dialog({
          title: 'Connection Failed',
          body: 'Connection failed.  Please try again.',
          onClose: () => {
            location.reload();
          },
        });
      }
    },
    stopScreenShare(result) {
      if (result.streamStopped) {
        screenEvents.disconnect.post({});
      }
    },
    setActiveMonitor(monitor: ChangedDisplay) {
      screenEvents.changedDisplay.post(monitor);
    },
    fullFrame(msg, sc) {
      console.log(
        'WebSockets: Full frame got (failed: ' + msg.frameFailed + ')',
      );
      if (!msg.frameFailed) {
        let result: FrameData = msg;
        let includedFrame = new Uint8Array(result.frameData);
        screenEvents.frameData.post(result);
        screenEvents.frame.post({
          fullScreen: true,
          isRender: true,
          x: 0,
          y: 0,
          top: 0,
          left: 0,
          bottom: result.screenBounds.height,
          right: result.screenBounds.width,
          image: includedFrame,
        });
      } else {
        screenEvents.frame.post(msg);
      }
    },
    clipboardData(msg: ClipboardData) {
      clipboardChanged(msg.text);
    },
    cursorIcon(msg: CursorInfo) {
      screenEvents.cursorIconChanged.post(msg);
    },
    screenShareData: function(msg: ScreenTile) {
      console.log('WebSockets: Tile got (full screen: ' + msg.fullScreen + ')');
      if (msg.fullScreen) {
        screenEvents.frameData.post({
          screenBounds: {
            top: msg.top,
            bottom: msg.bottom,
            left: msg.left,
            right: msg.right,
            x: msg.x,
            y: msg.y,
            height: msg.bottom - msg.top,
            width: msg.right - msg.left,
            isEmpty: false,
          },
          frameData: [],
        });
      }
      screenEvents.frame.post(msg);
    },
    getScreenList(msg) {
      console.log('getScreenList:', msg);
      screenEvents.displayListReceived.post(msg);
    },
    getSessionList(msg: WindowsSessionInfo) {
      console.log('Got Session List: ', msg);
      screenEvents.sessionListReceived.post(msg);
    },
    screenShareSessionChanged(msg) {
      console.log('screenShareSessionChanged:', msg);
      messageActions.message({
        style: 'info',
        text: 'Windows session changed.  Reconnecting...',
      });
    },
    relaunchingInTargetSession(msg) {
      console.log('relaunchingInTargetSession:', msg);
      messageActions.message({
        style: 'info',
        text:
          "Switching to target Windows session.  Please don't refresh the page.",
      });
    },
    requestAttendedAccessSettings(msg: AttendedAccessSettings) {
      screenEvents.attendedAccessSettingsReceived.post(msg);
    },
    declineScreenAccess(msg: string) {
      dialogEvents.dialog({
        title: 'Request Declined',
        body: 'The user declined your request for access.',
      });
    },
  });
  rS.listenKeys(endpointMatch, {
    clipboardData(msg: ClipboardData) {
      clipboardChanged(msg.text);
    },
    cursorIcon(msg: CursorInfo) {
      screenEvents.cursorIconChanged.post(msg);
    },
    screenShareData(msg: ScreenTile) {
      // console.debug('WebRtc: Tile got (full screen: ' + msg.fullScreen + ')');
      if (msg.fullScreen) {
        screenEvents.frameData.post({
          screenBounds: {
            top: msg.top,
            bottom: msg.bottom,
            left: msg.left,
            right: msg.right,
            x: msg.x,
            y: msg.y,
            height: msg.bottom - msg.top,
            width: msg.right - msg.left,
            isEmpty: false,
          },
          frameData: [],
        });
      }
      screenEvents.frame.post(msg);
      //delete msg.image;
    },
    fullFrame(msg, sc) {
      console.log('WebRtc: Full frame got (failed: ' + msg.frameFailed + ')');
      if (!msg.frameFailed) {
        let result: FrameData = msg;
        let includedFrame = new Uint8Array(result.frameData);
        screenEvents.frameData.post(result);
        screenEvents.frame.post({
          fullScreen: true,
          isRender: true,
          x: 0,
          y: 0,
          top: 0,
          left: 0,
          bottom: result.screenBounds.height,
          right: result.screenBounds.width,
          image: includedFrame,
        });
      } else {
        screenEvents.frame.post(msg);
      }
    },
  });

  function clipboardChanged(text: string) {
    console.log('clipboardData: ' + text);
    clipboardState.remoteClipboardText = text;
    screenEvents.clipboardChanged.post(text);
  }
  /*
    sC.listen(isTile, (msg: ScreenTile, sc) => {
        screenEvents.frame.post(msg)
    })
    */
  /*
    sC.listenBuffer(data => {
        //let compressedBuffer = base64toArray(data)
        //let imageBuffer = pako.inflate(compressedBuffer)
        //let base64Image = arrayToBase64(imageBuffer)
        screenEvents.frame(data)
    })
    */
}
